import { useState, useEffect, useCallback } from "react";
import { t } from "../i18n";

function getAuthHeaders() {
  const token = localStorage.getItem("fm_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function votePlaylist(playlistId, vote) {
  const res = await fetch(`/api/playlists/${playlistId}/vote`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ vote }),
  });
  if (!res.ok) {
    if (res.status === 401 || res.status === 403) return { needLogin: true };
    throw new Error("Vote failed");
  }
  return res.json();
}

export default function PlaylistsSection({ lang, onNavigate }) {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState({});

  const fetchRanked = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/playlists/public/ranked?limit=16", {
        headers: { ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setPlaylists(data.playlists || []);
    } catch {
      setPlaylists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRanked();
  }, [fetchRanked]);

  async function handleVote(plId, voteType) {
    const pl = playlists.find((p) => p.id === plId);
    if (!pl) return;

    // Optimistic: toggle
    const isSame = pl.user_vote === voteType;
    const newVote = isSame ? "remove" : voteType;
    const prevVote = pl.user_vote;

    setPlaylists((prev) =>
      prev.map((p) => {
        if (p.id !== plId) return p;
        let up = p.upvote_count;
        let down = p.downvote_count;
        if (prevVote === "up") up--;
        if (prevVote === "down") down--;
        if (newVote === "up") up++;
        if (newVote === "down") down++;
        return { ...p, upvote_count: up, downvote_count: down, user_vote: newVote, net_score: up - down };
      })
    );

    setVoting((v) => ({ ...v, [plId]: true }));
    try {
      const result = await votePlaylist(plId, newVote);
      if (result.needLogin) {
        // Revert
        setPlaylists((prev) =>
          prev.map((p) => {
            if (p.id !== plId) return p;
            return { ...p, user_vote: prevVote, upvote_count: pl.upvote_count, downvote_count: pl.downvote_count, net_score: pl.net_score };
          })
        );
        return;
      }
      // Sync from server
      setPlaylists((prev) =>
        prev.map((p) => {
          if (p.id !== plId) return p;
          return { ...p, upvote_count: result.upvotes ?? p.upvote_count, downvote_count: result.downvotes ?? p.downvote_count, user_vote: result.vote ?? newVote };
        })
      );
    } catch {
      // Revert
      setPlaylists((prev) =>
        prev.map((p) => {
          if (p.id !== plId) return p;
          return { ...p, user_vote: prevVote, upvote_count: pl.upvote_count, downvote_count: pl.downvote_count, net_score: pl.net_score };
        })
      );
    } finally {
      setVoting((v) => ({ ...v, [plId]: false }));
    }
  }

  if (loading) {
    return (
      <section id="playlists-section" className="bg-white border-y border-gray-200 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-2 border-gray-200 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="playlists-section"
      className="bg-white border-y border-gray-200 py-16"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              🔥 {t(lang, "hotPlaylists")}
            </h2>
            <p className="text-gray-500 text-sm">
              {t(lang, "hotPlaylistsDesc")}
            </p>
          </div>
          <button
            onClick={() => onNavigate("/playlists")}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium transition"
          >
            {t(lang, "viewAllPlaylists")} →
          </button>
        </div>
        {playlists.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {playlists.map((pl) => (
              <div
                key={pl.id}
                className="bg-gray-50 rounded-xl hover:bg-indigo-50 transition group cursor-pointer overflow-hidden"
              >
                {/* Clickable content area (everything except vote buttons) */}
                <div
                  onClick={() => onNavigate(`/playlist/${pl.id}`)}
                  className="p-5 pb-3"
                >
                  {pl.cover_url ? (
                    <img
                      src={pl.cover_url}
                      alt={pl.name}
                      className="w-full aspect-[3/2] rounded-lg object-cover mb-3 shadow-sm group-hover:shadow-md transition-shadow"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-2xl mb-2 opacity-60 group-hover:opacity-100 transition">🎵</div>
                  )}
                  <div className="font-semibold text-gray-900 mb-0.5 truncate text-base">
                    {pl.name}
                  </div>
                  <div className="text-xs text-gray-400 mb-1">
                    {pl.creator_name || "—"} · {pl.track_count || 0} {t(lang, "tracksInPlaylist")}
                  </div>
                </div>
                {/* Vote bar */}
                <div className="px-5 pb-3 flex items-center gap-3 border-t border-gray-200/60 pt-2.5">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleVote(pl.id, "up"); }}
                    disabled={voting[pl.id]}
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition ${
                      pl.user_vote === "up"
                        ? "bg-indigo-100 text-indigo-600"
                        : "text-gray-400 hover:text-indigo-500 hover:bg-indigo-50"
                    } disabled:opacity-50`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={pl.user_vote === "up" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                    {pl.upvote_count || 0}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleVote(pl.id, "down"); }}
                    disabled={voting[pl.id]}
                    className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md transition ${
                      pl.user_vote === "down"
                        ? "bg-red-50 text-red-500"
                        : "text-gray-400 hover:text-red-400 hover:bg-red-50"
                    } disabled:opacity-50`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill={pl.user_vote === "down" ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 5v14M5 12l7 7 7-7" />
                    </svg>
                    {pl.downvote_count || 0}
                  </button>
                  <div className="flex-1" />
                  <div className={`text-xs font-medium ${pl.net_score >= 0 ? "text-indigo-500" : "text-red-400"}`}>
                    {pl.net_score >= 0 ? "+" : ""}{pl.net_score}
                  </div>
                </div>
              </div>
            ))}
            <div
              onClick={() => onNavigate("/playlists")}
              className="p-6 bg-gray-50 rounded-xl hover:bg-indigo-50 hover:text-indigo-700 transition cursor-pointer border-2 border-dashed border-gray-200 hover:border-indigo-300 flex flex-col items-center justify-center text-gray-400 hover:text-indigo-500 min-h-[140px]"
            >
              <div className="text-2xl mb-2">+</div>
              <div className="text-sm font-medium">
                {t(lang, "viewAllPlaylists")}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-4xl mb-4 text-gray-300">🎵</div>
            <p className="text-gray-400 mb-4">{t(lang, "playlistEmpty")}</p>
          </div>
        )}
      </div>
    </section>
  );
}
